import {Button, Menu, MenuItem} from '@mui/material';
import {
  ComponentProps,
  JSXElementConstructor,
  ReactNode,
  useMemo,
  useState,
} from 'react';

import {useAppDispatch} from '../../../hooks/redux';
import {GasMonitoringNode} from '../../../interfaces/GasMonitoringNode';
import reduxActions from '../../../redux/actions';
import AccessControl from '../../common/AccessControl';
import HistoryReportMenuItem from '../../common/HistoryReportMenuItem';
import AMSSensorItemDisableEventsModal from './AMSSensorItemDisableEventsModal';

type ComponentType = keyof JSX.IntrinsicElements | JSXElementConstructor<any>;

interface Props<T extends ComponentType> {
  setViewType?: Function;
  onOpenItem?: Function;
  onOpenHistory?: Function;
  onToggleCalibrationMode?: Function;
  onToggleEventDelay?: Function;
  onToggleInputStatusEvent?: Function;
  onToggleAvaTest?: Function;
  onStandDown?: Function;
  onSubmitted?: () => void;
  component?: T;
  componentProps?: ComponentProps<T>;
  children?: ReactNode;
  item?: GasMonitoringNode;
}

const AMSSensorItemEditButton = <T extends ComponentType = typeof Button>({
  component,
  componentProps,
  children,
  onOpenItem,
  onOpenHistory,
  onToggleCalibrationMode,
  onToggleEventDelay,
  onToggleInputStatusEvent,
  onToggleAvaTest,
  onStandDown,
  onSubmitted,
  item,
}: // onPurged,
Props<T>) => {
  const reduxAppDispatch = useAppDispatch();
  const loadAssets = () => {
    reduxAppDispatch(reduxActions.assets.fetchAmsEmodules);
    reduxAppDispatch(reduxActions.assets.fetchGasMonitorNodes);
  };

  const Component = component ?? Button;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [action, setAction] = useState<'disable_events' | 'enable_events'>();
  const longTermHistoryAvailable = useMemo(() => {
    return !item?.non_sensing && !!item?.emodule_id;
  }, [item]);

  const allowCalibration = !!item?.emodules?.length;
  const allowEventDelay = !!item?.emodule_id;

  const isCalibrated = useMemo(() => {
    return item?.emodules.find((e) => e.id === item?.emodule_id)
      ?.calibration_mode_till;
  }, [item]);

  const isEventDelayEnabled = useMemo(() => {
    return !!item?.emodules.find((e) => e.id === item?.emodule_id)?.event_delay;
  }, [item]);

  return (
    <>
      <Component
        {...componentProps}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {children}
      </Component>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onBackdropClick={() => setAnchorEl(null)}
      >
        {(item?.type_id === 40 ||
          item?.type_id === 20 ||
          item?.type_id === 130 ||
          item?.type_id === 44) &&
          item?.disable_event === 0 && (
            <MenuItem
              onClick={() => {
                setAction('disable_events');
                setAnchorEl(null);
              }}
            >
              Disable Events
            </MenuItem>
          )}

        {(item?.type_id === 40 ||
          item?.type_id === 20 ||
          item?.type_id === 130 ||
          item?.type_id === 44) &&
          item?.disable_event !== 0 && (
            <MenuItem
              onClick={() => {
                setAction('enable_events');
                setAnchorEl(null);
              }}
            >
              Enable Events
            </MenuItem>
          )}

        <HistoryReportMenuItem
          type="amsShortTerm"
          id={`${item?.emodule_id ?? 0}-${item?.id ?? 0}`}
          onOpen={() =>
            onOpenHistory?.(
              `${item?.emodule_id ?? 0}-${item?.id ?? 0}`,
              'amsShortTerm'
            )
          }
          onAfterAction={() => setAnchorEl(null)}
        >
          Short Term History Report
        </HistoryReportMenuItem>
        {longTermHistoryAvailable ? (
          <HistoryReportMenuItem
            type="amsLongTerm"
            id={`${item?.emodule_id ?? 0}-${item?.id ?? 0}`}
            onOpen={() => {
              loadAssets();
              onOpenHistory?.(
                `${item?.emodule_id ?? 0}-${item?.id ?? 0}`,
                'amsLongTerm'
              );
            }}
            onAfterAction={() => setAnchorEl(null)}
          >
            Long Term History Report
          </HistoryReportMenuItem>
        ) : null}
        <HistoryReportMenuItem
          type="amsLocation"
          id={`${item?.emodule_id ?? 0}-${item?.id ?? 0}`}
          onOpen={() => {
            loadAssets();
            onOpenHistory?.(
              `${item?.emodule_id ?? 0}-${item?.id ?? 0}`,
              'amsLocation'
            );
          }}
          onAfterAction={() => setAnchorEl(null)}
        >
          Location Report
        </HistoryReportMenuItem>
        {allowCalibration ? (
          <AccessControl
            permissions={['patch::/ams/emodule/:id(\\d+)/calibration_mode']}
          >
            <MenuItem
              onClick={() => {
                onToggleCalibrationMode?.(item?.emodule_id, !isCalibrated);
                setAnchorEl(null);
              }}
            >
              {`${isCalibrated ? 'Disable' : 'Enable'}`} Calibration Mode
            </MenuItem>
          </AccessControl>
        ) : null}
        {allowEventDelay ? (
          <AccessControl
            permissions={[
              'patch::/ams/emodule/:id(\\d+)/event-delay/:channel(\\d+)',
            ]}
          >
            <MenuItem
              onClick={() => {
                onToggleEventDelay?.(item?.emodule_id, !isEventDelayEnabled);
                setAnchorEl(null);
              }}
            >
              {`${isEventDelayEnabled ? 'Disable' : 'Enable'}`} Event Delay
            </MenuItem>
          </AccessControl>
        ) : null}
        <AccessControl
          permissions={['patch::/ams/:id(\\d+)/input-status-event']}
        >
          <MenuItem
            onClick={() => {
              onToggleInputStatusEvent?.(item?.id, !item?.disable_input_event);
              setAnchorEl(null);
            }}
          >
            {`${!item?.disable_input_event ? 'Disable' : 'Enable'}`} Input
            Status Events
          </MenuItem>
        </AccessControl>
        <AccessControl
          permissions={['patch::/ams/:id(\\d+)/input-status-event']}
        >
          <MenuItem
            onClick={() => {
              onToggleInputStatusEvent?.(item?.id, !item?.disable_input_event);
              setAnchorEl(null);
            }}
          >
            {`${!item?.disable_input_event ? 'Disable' : 'Enable'}`} Input
            Status Events
          </MenuItem>
        </AccessControl>
        <AccessControl
          permissions={['patch::/ams/:id(\\d+)/stand_down']}
          accessChecker={() => !!item?.ava}
        >
          <MenuItem
            onClick={() => {
              onStandDown?.(item?.id);
              setAnchorEl(null);
            }}
          >
            Stand Down
          </MenuItem>
        </AccessControl>
        <AccessControl
          permissions={['patch::/ams/:id(\\d+)/ava_test']}
          accessChecker={() => !!item?.ava}
        >
          <MenuItem
            onClick={() => {
              onToggleAvaTest?.(item?.id, !item?.test_ava_running);
              setAnchorEl(null);
            }}
          >
            {`${item?.test_ava_running ? 'Cancel ' : ''}`} Test AVA
          </MenuItem>
        </AccessControl>
        <AccessControl permissions={['patch::/ams/:id(\\d+)']}>
          <MenuItem
            onClick={() => {
              loadAssets();
              onOpenItem?.(item?.id, 'ams', 'update');
              setAnchorEl(null);
            }}
          >
            Edit Sensor
          </MenuItem>
        </AccessControl>
        <MenuItem
          onClick={() => {
            onOpenItem?.(item?.id, 'ams', 'view');
            setAnchorEl(null);
          }}
        >
          Info & Actions
        </MenuItem>
      </Menu>

      {(action === 'disable_events' || action === 'enable_events') && (
        <AMSSensorItemDisableEventsModal
          item={item}
          action={action}
          open={action === 'disable_events' || action === 'enable_events'}
          onClose={() => {
            setAction(undefined);
          }}
          onSubmitted={onSubmitted}
        />
      )}
    </>
  );
};

export default AMSSensorItemEditButton;
